import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Edit,
  Plus,
  RefreshCw,
  Trash2,
  Users,
  Search,
  X,
  Layers,
  Inbox,
  Info,
  Loader2,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import http from '../api/http';
import { useAuth } from '../context/AuthContext';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';
import { ALL_FILIALES_ID } from '../data/filiales';

/* ------------------------------------------------------------------ */
/*  Styles partagés                                                    */
/* ------------------------------------------------------------------ */

const inputClass =
  'w-full px-4 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-900 ' +
  'text-gray-900 dark:text-white placeholder-gray-400 ' +
  'focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all';

const labelClass = 'mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-200';

const primaryBtn =
  'inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium bg-blue-600 text-white ' +
  'rounded-xl shadow-sm hover:bg-blue-700 active:scale-[0.98] transition-all ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 focus-visible:ring-offset-2 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100';

const secondaryBtn =
  'inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium bg-white dark:bg-gray-800 ' +
  'text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600 rounded-xl ' +
  'hover:bg-gray-50 dark:hover:bg-gray-700 active:scale-[0.98] transition-all ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/30';

const keyframes = `
@keyframes tp-modal-in { from { opacity: 0; transform: translateY(8px) scale(.98); } to { opacity: 1; transform: none; } }
@keyframes tp-fade-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes tp-toast-in { from { opacity: 0; transform: translateX(16px); } to { opacity: 1; transform: none; } }
.tp-modal-in { animation: tp-modal-in .18s ease-out; }
.tp-fade-in { animation: tp-fade-in .15s ease-out; }
.tp-toast-in { animation: tp-toast-in .2s ease-out; }
@media (prefers-reduced-motion: reduce) {
  .tp-modal-in, .tp-fade-in, .tp-toast-in { animation: none; }
}
`;

/* ------------------------------------------------------------------ */
/*  Petits composants                                                  */
/* ------------------------------------------------------------------ */

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-4 rounded-xl border border-gray-100 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300">
      <Icon className="h-5 w-5" />
    </div>
    <div className="min-w-0">
      <div className="text-2xl font-semibold leading-tight text-gray-900 dark:text-white">{value}</div>
      <div className="truncate text-sm text-gray-500 dark:text-gray-400">{label}</div>
    </div>
  </div>
);

const getPages = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) pages.push('…');
  for (let p = start; p <= end; p++) pages.push(p);
  if (end < total - 1) pages.push('…');
  pages.push(total);
  return pages;
};

const Pagination = ({ current, total, from, to, count, pageSize, onPageSize, onChange }) => (
  <div className="flex flex-col gap-3 border-t border-gray-100 px-4 py-4 dark:border-gray-700 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500 dark:text-gray-400">
      <span>
        Affichage de <span className="font-medium text-gray-700 dark:text-gray-200">{from}</span> à{' '}
        <span className="font-medium text-gray-700 dark:text-gray-200">{to}</span> sur{' '}
        <span className="font-medium text-gray-700 dark:text-gray-200">{count}</span>
      </span>
      <label className="flex items-center gap-2">
        Par page
        <select
          value={pageSize}
          onChange={(e) => onPageSize(Number(e.target.value))}
          className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200"
        >
          {[10, 20, 50].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </label>
    </div>
    {total > 1 && (
      <div className="flex items-center gap-1">
        <button
          onClick={() => onChange(Math.max(1, current - 1))}
          disabled={current === 1}
          aria-label="Page précédente"
          className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        {getPages(current, total).map((page, i) =>
          page === '…' ? (
            <span key={`dots-${i}`} className="px-2 text-gray-400">
              …
            </span>
          ) : (
            <button
              key={page}
              onClick={() => onChange(page)}
              aria-current={current === page ? 'page' : undefined}
              className={`h-9 min-w-[2.25rem] rounded-lg border px-3 text-sm font-medium transition-colors ${
                current === page
                  ? 'border-blue-600 bg-blue-600 text-white shadow-sm'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              {page}
            </button>
          )
        )}
        <button
          onClick={() => onChange(Math.min(total, current + 1))}
          disabled={current === total}
          aria-label="Page suivante"
          className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    )}
  </div>
);

const Modal = ({ title, subtitle, onClose, children }) => {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="tp-fade-in fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="tp-modal-in w-full max-w-md overflow-hidden rounded-2xl border border-blue-100 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-800"
      >
        <div className="flex items-start justify-between gap-4 border-b border-blue-100 bg-gradient-to-r from-blue-50 to-white px-6 py-4 dark:border-gray-700 dark:from-gray-900 dark:to-gray-800">
          <div>
            <h3 className="text-lg font-bold text-blue-900 dark:text-blue-200">{title}</h3>
            {subtitle && <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-white hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

const TypesPersonnel = () => {
  const { user } = useAuth();
  const { filialeId } = useFiliale();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [activites, setActivites] = useState([]);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [activiteFilter, setActiviteFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [toasts, setToasts] = useState([]);
  const [confirmState, setConfirmState] = useState(null);

  const isAllFiliales = filialeId === 'all' || filialeId === ALL_FILIALES_ID;

  const notify = useCallback((type, message) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, type, message }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4200);
  }, []);

  /* ---------- Chargement ---------- */
  const fetchData = async () => {
    try {
      setLoading(true);
      let endpoint = '/api/types-personnel';
      if (filialeId && filialeId !== 'all' && filialeId !== ALL_FILIALES_ID) {
        endpoint += `?filiale_id=${filialeId}`;
      }
      const response = await http.get(endpoint);
      setData(Array.isArray(response.data) ? response.data : []);

      // Charger les activités
      const activitesResponse = await http.get('/api/activites');
      setActivites(Array.isArray(activitesResponse.data) ? activitesResponse.data : []);
    } catch (error) {
      console.error('Erreur:', error);
      setData([]);
      setActivites([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filialeId]);

  /* ---------- Actions ---------- */
  const handleCreate = () => {
    setEditingItem(null);
    if (isAllFiliales) {
      notify(
        'warning',
        'Sélectionnez une filiale spécifique dans le Périmètre avant de créer un type de personnel.'
      );
      return;
    }
    setFormData({ filiale_id: parseInt(filialeId) });
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({ ...item });
    setShowModal(true);
  };

  const handleDelete = (item) => {
    setConfirmState({
      title: 'Supprimer ce type de personnel ?',
      message: `« ${item.libelle} » sera supprimé définitivement. Cette action est irréversible.`,
      onConfirm: async () => {
        try {
          await http.delete(`/api/types-personnel/${item.idtype_personnel}`);
          notify('success', 'Type de personnel supprimé avec succès');
          fetchData();
        } catch (error) {
          notify('error', 'Erreur lors de la suppression : ' + (error.response?.data?.error || 'Erreur inconnue'));
        }
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    try {
      setSaving(true);
      if (editingItem) {
        await http.put(`/api/types-personnel/${editingItem.idtype_personnel}`, formData);
        notify('success', 'Type de personnel modifié avec succès');
      } else {
        await http.post('/api/types-personnel', formData);
        notify('success', 'Type de personnel créé avec succès');
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      notify('error', 'Erreur : ' + (error.response?.data?.error || 'Erreur inconnue'));
    } finally {
      setSaving(false);
    }
  };

  /* ---------- Filtrage + pagination ---------- */
  const activitesDisponibles = useMemo(
    () => [...new Set(data.map((d) => d.activite).filter(Boolean))].sort(),
    [data]
  );

  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    return data.filter((item) => {
      const matchSearch =
        !term ||
        (item.libelle || '').toLowerCase().includes(term) ||
        (item.code || '').toLowerCase().includes(term) ||
        (item.activite || '').toLowerCase().includes(term);
      const matchActivite = !activiteFilter || item.activite === activiteFilter;
      return matchSearch && matchActivite;
    });
  }, [data, search, activiteFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, activiteFilter, pageSize, filialeId]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const indexOfLast = safePage * pageSize;
  const indexOfFirst = indexOfLast - pageSize;
  const currentItems = filtered.slice(indexOfFirst, indexOfLast);

  const totalPersonnel = data.reduce((sum, d) => sum + (Number(d.nombre_personnel) || 0), 0);
  const hasFilters = search || activiteFilter;

  /* ---------- Rendu ---------- */
  return (
    <div className="space-y-6 p-4 sm:p-6">
      <style>{keyframes}</style>

      <BlueHeader
        title="Types de personnel"
        description="Gestion des types de personnel"
        icon={Users}
        actions={[
          {
            label: 'Créer',
            icon: Plus,
            onClick: handleCreate,
            primary: true
          },
          {
            label: 'Actualiser',
            icon: RefreshCw,
            onClick: fetchData,
            primary: false
          }
        ]}
      />

      {/* Bandeau contexte filiale */}
      {isAllFiliales && (
        <div className="flex items-start gap-3 rounded-xl border border-blue-100 bg-blue-50/70 px-4 py-3 text-sm text-blue-900 dark:border-blue-900 dark:bg-blue-900/20 dark:text-blue-200">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-600 dark:text-blue-300" />
          <p>
            Vous consultez toutes les filiales. Choisissez une filiale dans le Périmètre pour pouvoir créer un type de
            personnel.
          </p>
        </div>
      )}

      {/* Statistiques */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard icon={Users} label="Types de personnel" value={loading ? '–' : data.length} />
        <StatCard icon={Layers} label="Activités représentées" value={loading ? '–' : activitesDisponibles.length} />
        <StatCard icon={Users} label="Personnes rattachées" value={loading ? '–' : totalPersonnel} />
      </div>

      {/* Tableau */}
      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="flex flex-col gap-3 border-b border-gray-100 px-4 py-4 dark:border-gray-700 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Liste des types</h3>
            <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
              Définissez les catégories de personnel et leur activité.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <select
              value={activiteFilter}
              onChange={(e) => setActiviteFilter(e.target.value)}
              aria-label="Filtrer par activité"
              className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200"
            >
              <option value="">Toutes les activités</option>
              {activitesDisponibles.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un type, un code…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-10 pr-9 text-sm text-gray-900 placeholder-gray-400 transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-900 dark:text-white sm:w-64"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  aria-label="Effacer la recherche"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:text-gray-700 dark:hover:text-white"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-center gap-6 px-6 py-4">
                <div className="h-4 w-8 animate-pulse rounded bg-gray-100 dark:bg-gray-700" />
                <div className="h-5 w-20 animate-pulse rounded-full bg-gray-100 dark:bg-gray-700" />
                <div className="h-4 flex-1 animate-pulse rounded bg-gray-100 dark:bg-gray-700" />
                <div className="h-4 w-24 animate-pulse rounded bg-gray-100 dark:bg-gray-700" />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center px-6 py-14 text-center">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-500 dark:bg-blue-900/40 dark:text-blue-300">
              {hasFilters ? <Search className="h-7 w-7" /> : <Inbox className="h-7 w-7" />}
            </div>
            <h4 className="text-base font-semibold text-gray-800 dark:text-white">
              {hasFilters ? 'Aucun résultat' : 'Aucun type de personnel'}
            </h4>
            <p className="mt-1 max-w-sm text-sm text-gray-500 dark:text-gray-400">
              {hasFilters
                ? 'Aucun type ne correspond à votre recherche ou à ce filtre.'
                : 'Créez votre premier type de personnel pour organiser vos équipes.'}
            </p>
            <div className="mt-5">
              {hasFilters ? (
                <button
                  onClick={() => {
                    setSearch('');
                    setActiviteFilter('');
                  }}
                  className={secondaryBtn}
                >
                  Réinitialiser les filtres
                </button>
              ) : (
                <button onClick={handleCreate} className={primaryBtn}>
                  <Plus className="h-4 w-4" />
                  Créer un type
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-50/80 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:bg-gray-900/60 dark:text-gray-400">
                  <th className="px-6 py-3">ID</th>
                  <th className="px-6 py-3">Code</th>
                  <th className="px-6 py-3">Type de personnel</th>
                  <th className="px-6 py-3">Activité</th>
                  <th className="px-6 py-3">Personnel</th>
                  <th className="px-6 py-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {currentItems.map((item) => (
                  <tr
                    key={item.idtype_personnel}
                    className="transition-colors hover:bg-blue-50/40 dark:hover:bg-gray-700/60"
                  >
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-400">#{item.idtype_personnel}</td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <span className="inline-flex items-center rounded-md bg-green-100 px-2.5 py-1 font-mono text-xs font-semibold text-green-700 dark:bg-green-900/50 dark:text-green-300">
                        {item.code || '-'}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center gap-3">
                        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300">
                          <Users className="h-4 w-4" />
                        </span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{item.libelle}</span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700 dark:text-gray-200">
                      {item.activite || <span className="text-gray-300 dark:text-gray-600">-</span>}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-100 px-2.5 py-1 text-xs font-medium text-blue-700 dark:bg-blue-900/50 dark:text-blue-300">
                        <Users className="h-3 w-3" />
                        {item.nombre_personnel || 0}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex justify-center gap-1">
                        <button
                          type="button"
                          onClick={() => handleEdit(item)}
                          title="Modifier"
                          aria-label="Modifier"
                          className="rounded-lg p-2 text-blue-600 transition-colors hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-blue-900/40"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(item)}
                          title="Supprimer"
                          aria-label="Supprimer"
                          className="rounded-lg p-2 text-red-500 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <Pagination
            current={safePage}
            total={totalPages}
            from={indexOfFirst + 1}
            to={Math.min(indexOfLast, filtered.length)}
            count={filtered.length}
            pageSize={pageSize}
            onPageSize={setPageSize}
            onChange={setCurrentPage}
          />
        )}
      </div>

      {/* Modal création / modification */}
      {showModal && (
        <Modal
          title={`${editingItem ? 'Modifier' : 'Créer'} Type de personnel`}
          subtitle="Le code identifie le type de façon unique."
          onClose={() => setShowModal(false)}
        >
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 px-6 py-5">
              <div>
                <label className={labelClass}>Code</label>
                <input
                  type="text"
                  autoFocus
                  className={`${inputClass} font-mono tracking-wide`}
                  value={formData.code || ''}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="Ex: STAGIAIRE, CDD, CDI, GMC, BACKOFFICE"
                  required
                />
                <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                  Code unique pour identifier ce type de personnel (ex: STAGIAIRE, CDD, CDI, GMC, BACKOFFICE)
                </p>
              </div>
              <div>
                <label className={labelClass}>Libellé</label>
                <input
                  type="text"
                  className={inputClass}
                  value={formData.libelle || ''}
                  onChange={(e) => setFormData({ ...formData, libelle: e.target.value })}
                  placeholder="Ex: Stagiaire"
                  required
                />
              </div>
              <div>
                <label className={labelClass}>Activité</label>
                <select
                  className={inputClass}
                  value={formData.idactivite || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, idactivite: e.target.value ? parseInt(e.target.value) : '' })
                  }
                  required
                >
                  <option value="">Sélectionner une activité</option>
                  {activites.map((activite) => (
                    <option key={activite.idactivites} value={activite.idactivites}>
                      {activite.Libelle_activite}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 border-t border-blue-100 bg-gray-50/60 px-6 py-4 dark:border-gray-700 dark:bg-gray-900/40">
              <button type="button" onClick={() => setShowModal(false)} className={secondaryBtn}>
                Annuler
              </button>
              <button type="submit" disabled={saving} className={primaryBtn}>
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {editingItem ? 'Modifier' : 'Créer'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Confirmation de suppression (remplace window.confirm) */}
      {confirmState && (
        <Modal title={confirmState.title} onClose={() => setConfirmState(null)}>
          <div className="flex items-start gap-3 px-6 py-5">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-300">
              <AlertTriangle className="h-5 w-5" />
            </span>
            <p className="pt-1 text-sm text-gray-600 dark:text-gray-300">{confirmState.message}</p>
          </div>
          <div className="flex justify-end gap-3 border-t border-blue-100 bg-gray-50/60 px-6 py-4 dark:border-gray-700 dark:bg-gray-900/40">
            <button type="button" onClick={() => setConfirmState(null)} className={secondaryBtn}>
              Annuler
            </button>
            <button
              type="button"
              onClick={() => {
                const { onConfirm } = confirmState;
                setConfirmState(null);
                onConfirm();
              }}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:bg-red-700 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/40"
            >
              <Trash2 className="h-4 w-4" />
              Supprimer
            </button>
          </div>
        </Modal>
      )}

      {/* Notifications */}
      <div
        className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-full max-w-sm flex-col gap-2"
        aria-live="polite"
      >
        {toasts.map((t) => {
          const styles = {
            success: { box: 'border-green-200 dark:border-green-800', icon: CheckCircle2, color: 'text-green-600' },
            error: { box: 'border-red-200 dark:border-red-800', icon: AlertCircle, color: 'text-red-600' },
            warning: { box: 'border-amber-200 dark:border-amber-800', icon: AlertTriangle, color: 'text-amber-600' }
          }[t.type];
          const Icon = styles.icon;
          return (
            <div
              key={t.id}
              className={`tp-toast-in pointer-events-auto flex items-start gap-3 rounded-xl border bg-white px-4 py-3 shadow-lg dark:bg-gray-800 ${styles.box}`}
            >
              <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${styles.color}`} />
              <p className="flex-1 text-sm text-gray-800 dark:text-gray-100">{t.message}</p>
              <button
                onClick={() => setToasts((all) => all.filter((x) => x.id !== t.id))}
                aria-label="Fermer la notification"
                className="text-gray-400 hover:text-gray-700 dark:hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TypesPersonnel;